import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../../../../hooks/useTenant";
import { PoolPrintableReportLayout } from "../../../../../components/Common";

/**
 * MemberEvaluationsPrintModal
 * Componente de visualização e impressão do histórico de avaliações do aluno.
 * Reutiliza o PoolPrintableReportLayout para seguir perfeitamente o estilo do Modo Piscina A4.
 */
const MemberEvaluationsPrintModal = ({
    isOpen,
    toggle,
    member,
    allEvaluations = []
}) => {
    const { tenantName, tenantLogo } = useTenant();
    const printRef = useRef(null);

    const studentName = member ? `${member.firstName || ""} ${member.lastName || ""}`.trim() || member.fullName || member.name : "Aluno";

    // Configurando react-to-print para o formato A4 Retrato
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Avaliacoes_${studentName.replace(/\s+/g, "_")}`,
        pageStyle: `
            @page {
                size: A4 portrait;
                margin: 15mm 10mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    background-color: #ffffff !important;
                    color: #333 !important;
                }
            }
        `
    });

    const dateStr = new Date().toLocaleDateString("pt-BR");
    const timeStr = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    const safeEvaluations = Array.isArray(allEvaluations) ? allEvaluations : [];

    // Ordena as avaliações por data decrescente (mais recente primeiro)
    const sortedEvaluations = [...safeEvaluations].sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
    });

    // Detalhes do sub-header compactos
    const details = [
        {
            content: (
                <>
                    <strong>Identificação do Aluno:</strong> {studentName} 
                    {member?.document && ` | CPF: ${member.document}`}
                    {member?.phone && ` | Tel: ${member.phone}`}
                    {member?.email && ` | E-mail: ${member.email}`}
                </>
            )
        },
        {
            content: (
                <>
                    <strong>Resumo Técnico:</strong> Registros: {sortedEvaluations.length} | Data de Geração: {dateStr} às {timeStr}
                </>
            )
        }
    ];

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            centered
            contentClassName="border-0 shadow-lg position-relative overflow-hidden"
            backdropClassName="bg-dark bg-opacity-75"
            dialogClassName="neo-print-modal-dialog"
        >
            {/* Cabeçalho Interativo do Modal (apenas tela) */}
            <div className="no-print neo-print-modal-header-bar" style={{ padding: "15px 25px" }}>
                <div>
                    <h5 className="neo-print-modal-header-title">
                        <i className="mdi mdi-printer text-accent me-2" />
                        Visualização de Impressão — Histórico de Avaliações
                    </h5>
                    <p className="neo-print-modal-header-subtitle">
                        Confira o relatório formatado das avaliações do aluno. O estilo segue o padrão do Modo Piscina A4.
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <button
                        onClick={handlePrint}
                        className="btn btn-primary d-flex align-items-center gap-2 px-3 fw-bold shadow-sm"
                        style={{ borderRadius: "4px" }}
                    >
                        <i className="mdi mdi-printer" /> Imprimir Relatório
                    </button>
                    <button
                        onClick={toggle}
                        className="btn btn-light px-3 fw-semibold border"
                        style={{ borderRadius: "4px" }}
                    >
                        Fechar
                    </button>
                </div>
            </div>

            {/* Visualização Visual do Relatório */}
            <ModalBody className="neo-print-modal-body" style={{ backgroundColor: "#faf8f5", padding: "20px" }}>
                {/* Wrapper decorativo apenas para a tela */}
                <div style={{
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    overflow: "hidden",
                    margin: "0 auto",
                    maxWidth: "800px"
                }}>
                    <PoolPrintableReportLayout
                        ref={printRef}
                        tenantName={tenantName}
                        tenantLogo={tenantLogo}
                        title="Histórico de Avaliações Técnicas"
                        showTenantOnLeft={false}
                        titleSize="16px"
                        meta={
                            <>
                                <span style={{ 
                                    display: "block", 
                                    fontSize: "11px", 
                                    fontWeight: "700", 
                                    color: "#5f6c7b", 
                                    textTransform: "uppercase", 
                                    marginBottom: "4px" 
                                }}>
                                    {tenantName}
                                </span>
                                <strong>Aluno:</strong> {studentName}
                            </>
                        }
                        details={details}
                        footerNote="NeoApp • relatório de avaliações | contato@gestaoneo.com"
                    >
                        {/* TABELA DE HISTÓRICO */}
                        <div className="pool-print-blocks" style={{ marginBottom: "30px" }}>
                            <table className="pool-print-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "100px" }}>Data</th>
                                        <th>Avaliação / Evento</th>
                                        <th style={{ width: "160px" }}>Modalidade</th>
                                        <th style={{ width: "100px", textAlign: "center" }}>Progresso</th>
                                        <th style={{ width: "120px", textAlign: "center" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedEvaluations.length > 0 ? (
                                        sortedEvaluations.map((evaluation, i) => {
                                            if (!evaluation) return null;
                                            const displayNum = sortedEvaluations.length - i;
                                            return (
                                                <tr key={evaluation.id || i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                                                    <td>
                                                        {evaluation.date ? moment(evaluation.date).format("DD/MM/YYYY") : "—"}
                                                    </td>
                                                    <td style={{ fontWeight: "600" }}>
                                                        {displayNum}ª — {evaluation.eventName || "Avaliação Técnica"}
                                                    </td>
                                                    <td>
                                                        {evaluation.activityName || "—"}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: "600", color: "#2980b9" }}>
                                                        {evaluation.progressPercent !== undefined ? `${evaluation.progressPercent}%` : "0%"}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <span style={{ 
                                                            fontSize: "11px", 
                                                            fontWeight: "bold", 
                                                            backgroundColor: evaluation.status === "PASSED" ? "#e8f5e9" : "#fff3cd", 
                                                            color: evaluation.status === "PASSED" ? "#27ae60" : "#856404", 
                                                            padding: "2px 8px", 
                                                            borderRadius: "4px" 
                                                        }}>
                                                            {evaluation.status === "PASSED" ? "Aprovado" : "Em Progresso"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: "center", color: "#666" }}>
                                                Nenhuma avaliação cadastrada para este aluno.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* DETALHAMENTO DE CADA AVALIAÇÃO */}
                        {sortedEvaluations.length > 0 && (
                            <div className="pool-print-blocks">
                                <h5 style={{ fontSize: "15px", fontWeight: "800", color: "#2c3e50", textTransform: "uppercase", borderBottom: "2px solid #333", paddingBottom: "6px", marginBottom: "15px" }}>
                                    Objetivos
                                </h5>

                                {sortedEvaluations.map((evaluation, idx) => {
                                    if (!evaluation) return null;
                                    const displayNum = sortedEvaluations.length - idx;
                                    const grouped = {};
                                    if (evaluation.objectives && Array.isArray(evaluation.objectives)) {
                                        evaluation.objectives.forEach(obj => {
                                            if (obj) {
                                                grouped[obj.name || "Geral"] = obj.topics || [];
                                            }
                                        });
                                    }

                                    return (
                                        <section 
                                            key={evaluation.id || idx} 
                                            className="pool-print-block" 
                                        >
                                            {/* Título do Bloco de Avaliação */}
                                            <div className="pool-print-block-title">
                                                <strong>
                                                    {displayNum}ª — {evaluation.eventName || "Avaliação Técnica"}
                                                </strong>
                                                <span style={{ 
                                                    backgroundColor: evaluation.status === "PASSED" ? "#e8f5e9" : "#fff3cd", 
                                                    color: evaluation.status === "PASSED" ? "#27ae60" : "#856404", 
                                                    padding: "2px 8px", 
                                                    borderRadius: "4px" 
                                                }}>
                                                    {evaluation.status === "PASSED" ? "Aprovado" : "Em Progresso"} 
                                                    {evaluation.progressPercent !== undefined ? ` (${evaluation.progressPercent}%)` : ""}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#666", marginBottom: "8px" }}>
                                                Data da Avaliação: {evaluation.date ? moment(evaluation.date).format("DD/MM/YYYY") : "—"}
                                                {evaluation.activityName && ` | Modalidade: ${evaluation.activityName}`}
                                            </div>

                                            {Object.keys(grouped).length > 0 ? (
                                                <table className="pool-print-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Tópico</th>
                                                            <th style={{ width: "150px", textAlign: "right" }}>Resultado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(grouped).map(([objName, topics], oIdx) => (
                                                            <React.Fragment key={oIdx}>
                                                                <tr style={{ backgroundColor: "#fcfcfc" }}>
                                                                    <td colSpan="2" style={{ fontWeight: "700", color: "#444" }}>
                                                                        {objName}
                                                                    </td>
                                                                </tr>
                                                                {Array.isArray(topics) && topics.map((topic, tIdx) => {
                                                                    if (!topic) return null;
                                                                    return (
                                                                        <tr key={tIdx} style={{ backgroundColor: tIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
                                                                            <td style={{ paddingLeft: "20px", color: "#555" }}>
                                                                                {topic.name || "—"}
                                                                                {topic.isFundamental && (
                                                                                    <span style={{ 
                                                                                        fontSize: "9px", 
                                                                                        fontWeight: "bold", 
                                                                                        backgroundColor: "#fde8e8", 
                                                                                        color: "#e53e3e", 
                                                                                        padding: "1px 4px", 
                                                                                        borderRadius: "2px",
                                                                                        marginLeft: "6px"
                                                                                    }}>
                                                                                        Fund.
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td style={{ textAlign: "right", fontWeight: "600", color: "#2c3e50" }}>
                                                                                {topic.levelName || "Avaliado"}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p style={{ margin: "4px 0", fontSize: "11px", fontStyle: "italic", color: "#666" }}>
                                                    Nenhum tópico detalhado disponível.
                                                </p>
                                            )}

                                            {evaluation.observations && (
                                                <div style={{ 
                                                    fontSize: "11px", 
                                                    fontStyle: "italic", 
                                                    marginTop: "8px", 
                                                    padding: "6px 10px", 
                                                    backgroundColor: "#fdf8f2", 
                                                    borderLeft: "3px solid #e67e22",
                                                    color: "#555" 
                                                }}>
                                                    <strong>Observações:</strong> {evaluation.observations}
                                                </div>
                                            )}
                                        </section>
                                    );
                                })}
                            </div>
                        )}
                    </PoolPrintableReportLayout>
                </div>
            </ModalBody>
        </Modal>
    );
};

MemberEvaluationsPrintModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    member: PropTypes.object,
    allEvaluations: PropTypes.array
};

export default MemberEvaluationsPrintModal;
